import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Box, Grid, FormControlLabel, Checkbox, InputAdornment, IconButton, Alert,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import TireScanner from './TireScanner';
import {
  TIRE_BRANDS, TIRE_COMPOUNDS, TIRE_STATUSES, TIRE_POSITIONS,
  createTire, updateTire,
} from '../../utils/tiresService';

const EMPTY = {
  barcode: '', brand: '', compound: '', dotCode: '', dimensions: '',
  vehicleId: '', position: '', status: 'new',
  kmTotal: 0, heatCycles: 0,
  lifeMaxKm: '', lifeMaxCycles: '',
  sealed: false, notes: '',
};

const FC_SX = { minWidth: 200 };

export default function TireForm({ open, onClose, onSaved, tire, teamId, userId, vehicles = [] }) {
  const { t } = useTranslation('tires');
  const isEdit = !!tire?.id;
  const [data, setData]   = useState(EMPTY);
  const [scanner, setScanner] = useState(false);
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setData(tire ? { ...EMPTY, ...tire } : EMPTY);
      setError('');
    }
  }, [open, tire]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setError('');
    if (!data.barcode?.trim()) {
      setError(t('form_error_barcode_required'));
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateTire(tire.id, data);
      } else {
        await createTire(teamId, userId, data);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || t('form_error_save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEdit ? t('edit_tire') : t('add_tire')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Barcode + bottone scanner */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('form_field_barcode')}
                value={data.barcode}
                onChange={e => set('barcode', e.target.value)}
                disabled={isEdit}
                InputProps={{
                  endAdornment: !isEdit && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setScanner(true)} color="primary" edge="end">
                        <QrCodeScannerIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" sx={FC_SX}>
                <InputLabel>{t('form_field_brand')}</InputLabel>
                <Select value={data.brand} label={t('form_field_brand')} onChange={e => set('brand', e.target.value)}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {TIRE_BRANDS.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" sx={FC_SX}>
                <InputLabel>{t('form_field_compound')}</InputLabel>
                <Select value={data.compound} label={t('form_field_compound')} onChange={e => set('compound', e.target.value)}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {TIRE_COMPOUNDS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={t('form_field_dot')} value={data.dotCode} onChange={e => set('dotCode', e.target.value)} placeholder={t('form_field_dot_placeholder')} sx={FC_SX} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={t('form_field_dimensions')} value={data.dimensions} onChange={e => set('dimensions', e.target.value)} placeholder={t('form_field_dimensions_placeholder')} sx={FC_SX} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" sx={FC_SX}>
                <InputLabel>{t('form_field_status')}</InputLabel>
                <Select value={data.status} label={t('form_field_status')} onChange={e => set('status', e.target.value)}>
                  {TIRE_STATUSES.map(s => <MenuItem key={s.key} value={s.key}>{t(`status_${s.key}`, s.key)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" sx={FC_SX}>
                <InputLabel>{t('form_field_vehicle')}</InputLabel>
                <Select value={data.vehicleId || ''} label={t('form_field_vehicle')} onChange={e => {
                  const v = e.target.value || null;
                  set('vehicleId', v);
                  if (!v) set('position', '');
                }}>
                  <MenuItem value=""><em>{t('form_vehicle_none')}</em></MenuItem>
                  {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{v.modello || v.name || v.id}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Posizione di montaggio: visibile solo se è stata scelta una vettura */}
            {data.vehicleId && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('form_position_prompt')}
                  </Typography>
                  <ToggleButtonGroup
                    value={data.position || ''}
                    exclusive
                    onChange={(_, v) => set('position', v || '')}
                    size="small"
                    fullWidth
                  >
                    {TIRE_POSITIONS.map(p => (
                      <ToggleButton key={p} value={p} sx={{ fontWeight: 700 }}>{p}</ToggleButton>
                    ))}
                    <ToggleButton value="" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{t('form_position_dismounted')}</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontSize: 10 }}>
                    {t('form_position_legend')}
                  </Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label={t('form_field_km')}
                value={data.kmTotal} onChange={e => set('kmTotal', e.target.value)} sx={FC_SX} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label={t('form_field_cycles')}
                value={data.heatCycles} onChange={e => set('heatCycles', e.target.value)} sx={FC_SX} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label={t('form_field_life_max_km')}
                value={data.lifeMaxKm} onChange={e => set('lifeMaxKm', e.target.value)} placeholder={t('form_field_life_max_km_placeholder')} sx={FC_SX} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label={t('form_field_life_max_cycles')}
                value={data.lifeMaxCycles} onChange={e => set('lifeMaxCycles', e.target.value)} placeholder={t('form_field_life_max_cycles_placeholder')} sx={FC_SX} />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={!!data.sealed} onChange={e => set('sealed', e.target.checked)} />}
                label={t('form_field_sealed')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} size="small" label={t('form_field_notes')}
                value={data.notes} onChange={e => set('notes', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t('saving') : (isEdit ? t('save_changes') : t('add_tire'))}
          </Button>
        </DialogActions>
      </Dialog>

      <TireScanner
        open={scanner}
        onClose={() => setScanner(false)}
        onCapture={(code) => {
          set('barcode', code);
          setScanner(false);
        }}
      />
    </>
  );
}
